import React from "react";
import { useNavigate } from "react-router-dom";
import { Flower2, ArrowRight } from "lucide-react";
import { ROSE_IMAGES } from "@/lib/roses";
import { Petals } from "@/components/Petals";
import { NotePicker } from "@/components/NotePicker";

const ROSES = [
  { color: "pink", title: "Pink Rose", img: ROSE_IMAGES.pink, meaning: "Gentle affection, elegance and secret admiration." },
  { color: "red", title: "Deep Red Rose", img: ROSE_IMAGES.red, meaning: "Intense romantic passion, devotion and courage." },
  { color: "white", title: "Pure White Rose", img: ROSE_IMAGES.white, meaning: "Graceful intention, truth and timeless reverence." },
];

export default function Roses() {
  const navigate = useNavigate();
  return (
    <div className="garden-bg">
      <Petals count={8} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 pb-8 text-center">
        <p className="text-xs font-mono tracking-[0.3em] uppercase text-pink-400 mb-4">The Rose Nursery</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-pink-50 text-glow">Roses, at 99p each</h1>
        <p className="max-w-2xl mx-auto mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
          Anyone can gift roses. Pick pink, white or red — attach up to three to any note and they'll
          bloom into a bouquet behind the words. One rose fills the backdrop; two or three become a bouquet.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {ROSES.map((r) => (
            <div
              key={r.color}
              data-testid={`rose-shop-card-${r.color}`}
              className="group rounded-2xl overflow-hidden border border-pink-500/25 bg-black/30 transition-all hover:border-pink-400 hover:shadow-[0_0_30px_rgba(255,46,147,0.25)] hover:-translate-y-1"
            >
              <div className="relative h-64 overflow-hidden">
                <img src={r.img} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <span className="absolute top-4 right-4 bg-black/70 border border-pink-500/40 text-pink-200 rounded-full px-3 py-1 text-sm font-mono">£0.99</span>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-pink-100 flex items-center gap-2">
                  <Flower2 className="w-5 h-5 text-pink-400" /> {r.title}
                </h3>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{r.meaning}</p>
              </div>
            </div>
          ))}
        </div>

        <NotePicker />
        <div className="mt-6 text-center">
          <button
            data-testid="roses-browse-garden-btn"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-pink-300 hover:text-pink-100 text-sm transition-colors"
          >
            Or browse the Garden <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Bouquet previews */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="rounded-2xl overflow-hidden border border-pink-500/20 relative h-56">
            <img src={ROSE_IMAGES.bouquet_two} alt="two roses" className="w-full h-full object-cover brightness-75" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-black/20 flex items-end p-6">
              <p className="font-serif text-2xl text-pink-50">Two roses → a duet bouquet</p>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-pink-500/20 relative h-56">
            <img src={ROSE_IMAGES.bouquet_three} alt="three roses" className="w-full h-full object-cover brightness-75" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-black/20 flex items-end p-6">
              <p className="font-serif text-2xl text-pink-50">Three roses → a full bouquet</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
